'use client';

import { useState } from 'react';

import { useTranslations, useLocaleLink } from '@fshr/context/i18n-context';
import { cn, getConfig } from '@fshr/shared-react';
import { Button, OTPInput } from '@fshr/components';

import getIntroContent from './get-intro-content';
import PersistUserCheckbox from './persist-user-checkbox';
import { useForm } from '@fshr/hooks';

interface SubmitOTPProps {
	isUserPersistent: boolean;
	sendCodeTo: string;
	messageType: 'email' | 'SMS' | 'short';
	disablePersistentLogin?: boolean;
	onSubmit: ({ otp, persistLogin }: { otp: string; persistLogin: boolean }) => void;
	reset: () => void;
}

export default function SubmitOTP({
	isUserPersistent,
	sendCodeTo,
	messageType,
	disablePersistentLogin = false,
	onSubmit,
	reset,
}: SubmitOTPProps) {
	const t = useTranslations();
	const localeLink = useLocaleLink();
	const isEmployeeMode = false;
	const { FSHR_PUBLIC_RESERVATIONS_BASE_URL } = getConfig();

	const [persistLogin, setPersistLogin] = useState(false);

	function handleOTPSubmit(formValues: Record<string, string>) {
		if (formValues.otp?.length !== 6) {
			return;
		}

		onSubmit({
			otp: formValues.otp,
			persistLogin,
		});
	}

	const introContent = getIntroContent({
		messageType,
		sendCodeTo,
		isYachtsMode: false,
		isUserPersistent,
		registerLink: isEmployeeMode
			? `${FSHR_PUBLIC_RESERVATIONS_BASE_URL}${localeLink('/employee-travel/register')}`
			: localeLink('/register'),
	});

	const showPersistentOption = !disablePersistentLogin && !isUserPersistent;

	const { registerField, handleSubmit } = useForm({ onSubmit: handleOTPSubmit });

	return (
		<div className={cn('w-full flex flex-col items-center', { 'px-400': isUserPersistent })}>
			<h1 className="mb-200 text-h2 text-center">{t('Verification Code')}</h1>

			<p
				className="mb-300 text-body2 text-center [&_a]:underline"
				dangerouslySetInnerHTML={{
					__html: t(introContent.translationKey, introContent?.translationParams),
				}}
			/>

			<form
				noValidate={true}
				onSubmit={handleSubmit}
				className="mb-400 flex flex-col gap-400 items-center"
			>
				<div className="flex flex-col items-center">
					<OTPInput
						{...registerField({
							name: 'otp',
							type: 'verificationCode',
							initialValue: '',
							options: {
								required: true,
								errorMessages: {
									empty: t('Verification Code is required.'),
									partial: t('Please enter a valid verification code.'),
								},
							},
						})}
					/>
				</div>

				{showPersistentOption && (
					<PersistUserCheckbox isChecked={persistLogin} onChange={setPersistLogin} />
				)}

				<div className="flex flex-row justify-center w-full ">
					<Button
						type="submit"
						className={cn(
							'',
							{ 'w-full lg:w-[220px]': !isUserPersistent },
							{ 'w-[130px]': isUserPersistent }
						)}
					>
						{t('Next')}
					</Button>
				</div>
			</form>

			<Button intent="text" onClick={reset}>
				{t('I didn’t receive my code')}
			</Button>
		</div>
	);
}
